import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogConfirmFooter,
} from "@/common/components/ui/dialog"
import Spinner from "../Spinner";
import { cn } from "@/common/utils/cn";
import { twMerge } from 'tailwind-merge';

export interface ModalProps {
  Trigger?: JSX.Element
  triggerDisabled?: boolean
  className?: string;
  title?: JSX.Element | string
  titleClass?: string
  subtitle?: JSX.Element | string
  subtitleClassName?: string
  body?: JSX.Element
  footer?: string
  footerClassName?: string
  footerIcon?: JSX.Element
  useCancellableFooter?: boolean
  confirmButtonText?: string
  confirmButtonClassName?: string
  confirmButtonIsLoading?: boolean
  spinnerClassName?: string
  cancelButtonText?: string
  cancelButtonClassName?: string
  disabled?: boolean
  onConfirm?: () => void
  onCancel?: () => void
  isOpen?: boolean
  setIsOpen?: (value: boolean) => void
  showClose?: boolean
  dataTestid?: string
  triggerAsChild?: boolean
  bodyClassName?: string
}

const Modal = ({
  Trigger,
  triggerDisabled,
  className,
  title,
  titleClass,
  subtitle,
  body,
  footer,
  footerClassName,
  footerIcon,
  useCancellableFooter,
  confirmButtonText,
  confirmButtonClassName,
  confirmButtonIsLoading,
  spinnerClassName,
  cancelButtonText,
  cancelButtonClassName,
  disabled,
  onConfirm,
  onCancel,
  isOpen,
  subtitleClassName = "",
  setIsOpen,
  showClose,
  dataTestid,
  triggerAsChild,
  bodyClassName,
}: ModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {Trigger && <DialogTrigger disabled={triggerDisabled} asChild={triggerAsChild}>{Trigger}</DialogTrigger>}
      <DialogContent className={className}>
        {title
          ? <DialogHeader showClose={showClose}>
            <DialogTitle data-testid="title_modal" className={titleClass}>{title}</DialogTitle>
            {subtitle
              ? <p data-testid="description_modal" className={`${subtitleClassName} text-sm`}>{subtitle}</p>
              : null
            }
          </DialogHeader>
          : null
        }
        {body
          ? <DialogDescription className={bodyClassName}>
            {body}
          </DialogDescription>
          : null
        }
        {useCancellableFooter
          ? <DialogConfirmFooter
            cancelButton={{
              children: cancelButtonText || 'Cancelar',
              className: cancelButtonClassName,
              disabled: disabled,
              onClick: onCancel,
            }}
            confirmButton={{
              children: (
                <div data-testid={(dataTestid || footer) ? dataTestid : 'confirm_modal_button'} className="flex items-center justify-center gap-4">
                  {confirmButtonText || 'Confirmar'}

                  {confirmButtonIsLoading && <Spinner className={cn("w-4 h-4 fill-white-800", spinnerClassName)} />}
                </div>
              ),
              className: confirmButtonClassName,
              disabled: disabled || confirmButtonIsLoading,
              onClick: onConfirm,
            }}
          />
          : footer
            ? <DialogFooter dataTestid={dataTestid} className={twMerge("flex items-center justify-center gap-4", disabled && 'bg-white-300', footerClassName)} onClick={() => !disabled && onConfirm?.()}>
              {footerIcon}

              {footer}

              {confirmButtonIsLoading && <Spinner className={cn("w-4 h-4 fill-black", spinnerClassName)} />}
            </DialogFooter>
            : null
        }
      </DialogContent>
    </Dialog>
  )
}

export default Modal
